/**
 * One-time MapLibre GL JS v6 process setup.
 *
 * Two things must happen exactly once, before the first `new Map(...)`:
 *
 *  1. `setWorkerUrl()`. v6 locates its source-processing worker from
 *     `import.meta.url`, which does not resolve inside a bundler's module
 *     graph. Without this call the worker never loads and the map renders
 *     nothing at all. The `?worker&url` query is required — plain `?url`
 *     emits the worker without its sibling `maplibre-gl-shared.mjs` and it
 *     dies on first import in a production build.
 *
 *  2. `addProtocol('pmtiles', ...)`. The protocol registry is global, so one
 *     registration covers every map instance on the page.
 *
 * v6 has no default export: `import maplibregl from 'maplibre-gl'` is a v5
 * pattern and fails here.
 */
import { addProtocol, removeProtocol, setWorkerUrl } from 'maplibre-gl';
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { Protocol } from 'pmtiles';

let initialised = false;
let protocol: Protocol | null = null;

export function initMapLibre(): void {
  if (initialised) return;
  initialised = true;
  setWorkerUrl(workerUrl);
  protocol = new Protocol();
  // `protocol.tile` already speaks the v4+ contract (async, resolves to
  // `{data}`); pmtiles >= 3 adapts itself, so no wrapper is needed.
  addProtocol('pmtiles', protocol.tile);
}

/** Only for tests and hot-reload teardown; the app never unloads the protocol. */
export function teardownMapLibre(): void {
  if (!initialised) return;
  removeProtocol('pmtiles');
  protocol = null;
  initialised = false;
}
